import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import {
	APP_INITIALIZER,
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	isDevMode,
	provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, Router, withDebugTracing } from "@angular/router";
import { provideEffects } from "@ngrx/effects";
import { provideRouterStore } from "@ngrx/router-store";
import { provideState, provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { HttpEnvironmentApiService } from "@partikelart/environment/data-access-cloud";
import { ENVIRONMENT_API, ENVIRONMENT_API_CONFIG } from "@partikelart/environment/data-access-core";
import { DemoEnvironmentApiService } from "@partikelart/environment/data-access-demo";
import { EXPORT_CONFIG } from "@partikelart/export/feature";
import { EXTRACTION_STEPS_API_CONFIG } from "@partikelart/extraction-steps/data-access";
import { USER_CONFIG, UserEffects, userReducer } from "@partikelart/identity/data-access-user";
import { PARTS_API_CONFIG as NEW_PARTS_API_CONFIG } from "@partikelart/parts/data-access-cloud";
import { PART_REPOSITORY } from "@partikelart/parts/data-access-core";
import { provideDemoPartRepository } from "@partikelart/parts/data-access-demo";
import { PartEffects, partFeature } from "@partikelart/parts/state";
import { BLOB_STORAGE } from "@partikelart/shared/util-blob-storage/core";
import { DemoBlobStorageService } from "@partikelart/shared/util-blob-storage/demo";
import { CLOUD_SPECIFICATION_API_CONFIG } from "@partikelart/specification/data-access-cloud";
import { SPECIFICATION_REPOSITORY } from "@partikelart/specification/data-access-core";
import { DemoSpecificationRepository } from "@partikelart/specification/data-access-demo";
import { SpecificationEffects, specificationFeature } from "@partikelart/specification/state";
import { SURFACE_REPORTS_API_CONFIG as REPORTS_API_CONFIG } from "@partikelart/surface/data-access";
import { SURFACE_REPORT_REPOSITORY } from "@partikelart/surface/data-access-core";
import { DemoSurfaceReportRepository } from "@partikelart/surface/data-access-demo";
import { SurfaceReportEffects, surfaceReportFeature } from "@partikelart/surface/state";
import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";
import * as Sentry from "@sentry/angular";
import { OAuthService } from "angular-oauth2-oidc";
import * as echarts from "echarts";
import { NgxEchartsModule } from "ngx-echarts";
import { providePrimeNG } from "primeng/config";
import { Subject } from "rxjs";
import { routes } from "./app.routes";
import { AppEnvironment, ENV_CONFIG } from "./app.token";
import { environment } from "./environments/environment";
import { GlobalErrorHandler } from "./global-error-handler";
import { portalApiInterceptor } from "./interceptors/portal-api.interceptor";
import { sentryMetaReducers } from "./sentry-meta.reducer";

const PartikelartPreset = definePreset(Aura, {
	semantic: {
		primary: {
			50: "#eef2ff",
			100: "#e0e7ff",
			200: "#c7d2fe",
			300: "#a5b4fc",
			400: "#818cf8",
			500: "#4f3a9c", // PartikelART Purple
			600: "#3e2c7d",
			700: "#2e1e5e",
			800: "#1e103f",
			900: "#0e0820",
			950: "#070410",
		},
	},
});

import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { ComparisonEffects, comparisonReducer } from "@partikelart/comparison/feature";
import { ExportEffects, reducer as exportReducer } from "@partikelart/export/feature";
import { MessageService } from "primeng/api";
import { BackendDemoInterceptor } from "./demo/backend-demo.interceptor";

/**
 * Demo app configuration.
 *
 * Uses in-memory demo repositories (DemoSpecificationRepository,
 * DemoSurfaceReportRepository, DemoPartRepository) and the
 * BackendDemoInterceptor so the app runs fully offline against mock data.
 *
 * This file replaces `app.config.ts` at build time via `fileReplacements`
 * in `project.json` for the `demo` configuration.
 */
export const appConfig: ApplicationConfig = {
	providers: [
		{ provide: ENV_CONFIG, useValue: environment as AppEnvironment },
		{ provide: USER_CONFIG, useValue: environment as AppEnvironment },
		{ provide: REPORTS_API_CONFIG, useValue: environment as AppEnvironment },
		{
			provide: ENVIRONMENT_API_CONFIG,
			useFactory: (env: AppEnvironment) => ({
				getPortalBackendUrl: () => env.portalBackendUrl ?? "",
			}),
			deps: [ENV_CONFIG],
		},
		{ provide: ENVIRONMENT_API, useClass: DemoEnvironmentApiService },
		{ provide: EXTRACTION_STEPS_API_CONFIG, useValue: environment as AppEnvironment },
		{ provide: EXPORT_CONFIG, useValue: environment as AppEnvironment },
		{ provide: CLOUD_SPECIFICATION_API_CONFIG, useValue: environment as AppEnvironment },
		{ provide: SPECIFICATION_REPOSITORY, useClass: DemoSpecificationRepository },
		// useExisting: the import workflow writes through the root singleton;
		// the token must alias the same in-memory store, not a second instance.
		{ provide: SURFACE_REPORT_REPOSITORY, useExisting: DemoSurfaceReportRepository },
		{ provide: NEW_PARTS_API_CONFIG, useValue: environment as AppEnvironment },
		provideDemoPartRepository(),
		{ provide: BLOB_STORAGE, useClass: DemoBlobStorageService },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: BackendDemoInterceptor,
			multi: true,
		},
		providePrimeNG({
			theme: {
				preset: PartikelartPreset,
			},
		}),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideAnimations(),
		MessageService,
		provideRouter(routes, withDebugTracing()),
		provideHttpClient(withInterceptorsFromDi(), withInterceptors([portalApiInterceptor])),
		provideTranslateService({
			loader: provideTranslateHttpLoader({
				resources: [
					{ prefix: "./assets/i18n/", suffix: "/common.json" },
					{ prefix: "./assets/i18n/", suffix: "/portal.json" },
				],
			}),
			fallbackLang: "en",
			lang: "en",
		}),
		provideStore(undefined, { metaReducers: sentryMetaReducers }),
		provideRouterStore(),
		provideStoreDevtools({
			maxAge: 25,
			logOnly: !isDevMode(),
			autoPause: true,
			trace: false,
			traceLimit: 75,
			connectInZone: true,
		}),
		provideState({ name: "user", reducer: userReducer }),
		provideState({ name: "comparison", reducer: comparisonReducer }),
		provideState({ name: "export", reducer: exportReducer }),
		provideState(surfaceReportFeature),
		provideState(specificationFeature),
		provideState(partFeature),
		provideEffects([
			UserEffects,
			ComparisonEffects,
			ExportEffects,
			PartEffects,
			SurfaceReportEffects,
			SpecificationEffects,
		]),
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler,
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		importProvidersFrom(
			NgxEchartsModule.forRoot({
				echarts,
			}),
		),
	],
};

/**
 * Stub OAuthService for demo mode without an identity provider.
 * All methods are no-ops — routes are unprotected and no token is required.
 */
function provideStubOAuthService() {
	const events$ = new Subject<any>();
	return {
		provide: OAuthService,
		useFactory: () => ({
			configure: () => {},
			tryLoginCodeFlow: () => Promise.resolve(),
			tryLogin: () => Promise.resolve(),
			hasValidAccessToken: () => false,
			getAccessToken: () => null,
			getRefreshToken: () => null,
			getIdToken: () => null,
			getIdentityClaims: () => null,
			getAccessTokenExpiration: () => null,
			initLoginFlow: () => {},
			logOut: () => {},
			silentRefresh: () => Promise.reject("no-op"),
			loadDiscoveryDocument: () => Promise.resolve(),
			loadUserProfile: () => Promise.reject("no-op"),
			setupAutomaticSilentRefresh: () => {},
			events: events$.asObservable(),
			state: null,
		}),
	};
}

appConfig.providers.push(provideStubOAuthService());
